import * as _ from "lodash";
import { DateTime } from "luxon";
import { toJS } from "mobx";
import { inject, observer } from "mobx-react";
import React, { Component } from "react";
import tinyColor from "tinycolor2";

import config from "../../../config";
import Icon from "../../../shared/Icon";
import { getUniqueOrderedSegments } from "../../Monitor/MonitorUtils";
import SurveilPatientStore from "./SurveilPatientStore";
import Chiclet from "../../../shared/Chiclet";
import "./SurveilPatient.scss";
import PatientItem from "../../../shared/Patient/PatientItem";
import { hasOffscreenCanvas } from "../../../shared/performanceUtils";
import resizeObserver from "../../../shared/resizeObserver";
import AhiScore from "../../AHI/AhiScore";
import className from "../../../shared/className";
import ColorPicker from "./components/ColorPicker";

class SurveilPatient extends Component {
  constructor(props) {
    super(props);
    this.asmpid = props.patient.asmpid;
    this.store = new SurveilPatientStore({
      asmpid: this.asmpid,
    });

    this.renderer = props.renderer;
    this.setChartSize = this.setChartSize.bind(this);
  }

  transferCanvas(_canvas) {
    const group = this.pmConfig?.waveGroups?.[0];
    if (_canvas && group && this.canvasTransferred !== this.canvasWorker) {
      const canvas = hasOffscreenCanvas
        ? _canvas.transferControlToOffscreen()
        : _canvas;
      this.canvasWorker.postMessage(
        {
          init: {
            canvas,
            visible: this.visible,
            waveGroups: [group],
            lineColor: this.lineColor,
            pixelsPerSecond: this.pixelsPerSecond,
            sweep: this.props.sweepMode,
            sweepHideDot: config.pm?.sweepHideDot,
            liveMode: true,
          },
        },
        [canvas],
      );
      this.canvasTransferred = this.canvasWorker;
      this.chartRect = undefined;
      this.setChartRect();
    }
  }

  get pixelsPerSecond() {
    return this.sweepCanvas?.width / config.pm.mpv.singleLeadSeconds;
  }

  componentDidMount() {
    if (!this.props.header && !this.props.patient.isEmptyBed) {
      (async () => {
        await this.props.surveilStore.configsPromise;
        this.store.config = await this.pmConfig;
        if (this.store.waveGroup) {
          this.props.renderWorker?.postMessage({
            initPatient: {
              asmpid: this.asmpid,
              waveGroup: toJS(this.store.waveGroup),
              visible: this.visible,
              lineColor: this.lineColor,
            },
          });
        } else {
          console.warn("missing waveGroup");
        }
      })();
    }

    if (this.refChart) {
      this.setChartSize();
      this.visible = true;
      this.props.surveilStore.setPatientVisible(this);
      // TODO: move this to  a single IntersectionObserver in Surveil.
      // change this.visible to a getter for the value in SurveilViewModel, or a prop.
      this.observer = new IntersectionObserver(
        (entries) => {
          const entry = _.maxBy(entries, "time");
          const visible = entry.isIntersecting;
          // When the element is re-sorted it is temporarily removed from the DOM, it will not be intersecting, and the
          // rootBounds will be null.  We want to ignore this case.
          if (entry.rootBounds === null) return;
          if (visible === this.visible) return;
          this.visible = visible;
          this.props.surveilStore.setPatientVisible(this);
          // TODO: combine messages for setPatientVisible, and setChartRect.
          if (this.props.sweepMode) {
            this.props.canvasWorker.postMessage({
              assignState: { visible },
            });
          }
          this.props.renderWorker?.postMessage({
            setPatientVisible: { asmpid: this.asmpid, visible },
          });
          this.setChartRect();
        },
        {
          root: this.props.parent.patientsDiv,
        },
      );
      this.observer.observe(this.refChart);
      this.transferCanvas(this.sweepCanvas);
      this.props.parent.eventTarget.addEventListener("resize", () => {
        if (!this.props.sweepMode) this.setChartRect();
      });
      resizeObserver.observe(this.ref, ({ contentBoxSize }) => {
        this.setChartRect();
        if (this.store.measurementData.length && this.visible) {
          this.canvasWorker?.postMessage({
            plotCharts: {
              waveGroups: [this.store.waveGroup],
              height: this.refChart.clientHeight,
              width: contentBoxSize.inlineSize,
              slicedWaveforms: this.store.measurementData,
              liveMode: true,
              pixelsPerSecond: this.pixelsPerSecond,
              chartHeight: 0,
              offsetY: 0,
              center: true,
              chartEndTime: this.store.serverTime
                .minus({ seconds: config.pm.surveilLeadBufferSeconds })
                .toISO(),
              paddingTop: 0,
            },
          });
        }
      });

      this.props.measurementsWorker.addEventListener("message", (event) => {
        if (!this.props.parent.visible) return;
        if (event.data && event.data.asmpid === this.asmpid) {
          this.store.updateValues(event.data);
        }
      });

      if (!hasOffscreenCanvas) throw "OffscreenCanvas not supported";

      this.setChartRect();
      this.refChart.offsetParent?.addEventListener("scroll", this.setChartRect);
    }
    this.alarmInterval = setInterval(() => this.store.updateAlarm(), 1000);
  }

  componentDidUpdate() {
    if (this.sweepCanvas) {
      if (this.props.sweepMode) {
        this.transferCanvas(this.sweepCanvas);
      }
      this.canvasWorkerDropped = false;
    } else {
      if (!this.canvasWorkerDropped) {
        this.canvasWorker?.postMessage({ dropInstance: true });
        this.canvasWorkerDropped = true;
      }
      this.canvasTransferred = false;
    }
  }

  plotMeasurements = (measurements) => {
    if (!measurements) return;
    this.store.bookmark = measurements.bookmark;
    this.parseNewWave({
      measurements,
      group: this.pmConfig?.waveGroups[0],
    });
  };

  componentWillUnmount() {
    if (this.observer) this.observer.disconnect();
    clearInterval(this.updateMeasurementsInterval);
    window.removeEventListener("resize", this.setChartRect);
    if (this.refChart) {
      this.refChart.offsetParent.removeEventListener(
        "scroll",
        this.setChartRect,
      );
      resizeObserver.unobserve(this.ref);
    }
    clearInterval(this.alarmInterval);
    if (!this.props.header)
      this.props.renderWorker?.postMessage({
        removePatient: {
          asmpid: this.asmpid,
        },
      });
    this.canvasWorker?.postMessage({ dropInstance: true });
    // TODO: Break out the header as a separate component, for focus-mode and Split screens.
    if (this.props.parent) {
      this.props.parent.surveilPatientRefs[this.asmpid] = undefined;
      this.props.parent.eventTarget.removeEventListener(
        "resize",
        this.setChartRect,
      );
    }
  }

  render() {
    const patient = this.props.patient;
    const moduleSpecific = this.props.moduleSpecifics;
    const alarm = this.props.alarm || this.store.highestAlarm || patient.alarm;
    return (
      <div
        className={`surveil-patient component ${
          this.props.smaller ? "smaller" : ""
        } ${
          !patient.isEmptyBed && alarm
            ? `${alarm.level && alarm.level.toLowerCase()}-alarm`
            : ""
        } ${this.props.header ? "header-mode" : ""} ${
          patient.isEmptyBed ? "empty-bed" : ""
        } `}
        onClick={this.props.onClick}
        style={{ fontSize: `${this.props.fontSize}px` }}
        ref={(ref) => (this.ref = ref)}
      >
        <header>
          {!patient.isEmptyBed && _.get(this.store.highestAlarm, "level") ? (
            <Icon
              className="alarm-icon"
              symbol={`alarm-${this.store.highestAlarm.level.toLowerCase()}`}
            />
          ) : null}
          <span className="message">
            {!patient.isEmptyBed &&
              _.get(this.store.highestAlarm, "message", "- - -")}
          </span>
          {this.props.header ? (
            <div className="close" onClick={this.props.close}>
              CLOSE
            </div>
          ) : null}
          <span className="time">{this.alarmTime}</span>
          {this.props.onRemove ? (
            <Icon symbol="x" onClick={this.props.onRemove} />
          ) : null}
        </header>

        <PatientItem
          patient={patient}
          patientIsActive={!patient.isEmptyBed}
          specifics={moduleSpecific}
          showPHI={this.props.surveilStore.showPHI}
          isCustomBedList={this.props.surveilStore.isCustomBedList}
        />

        {this.props.header ? null : (
          <React.Fragment>
            <div
              ref={(r) => (this.refChart = patient.isEmptyBed ? null : r)}
              className={"chart"}
              style={{
                aspectRatio: config.pm.mpv.singleLeadSeconds.toString(10),
              }}
            >
              <ColorPicker
                colorMap={this.props.surveilStore.colorMap}
                color={this.lineColor}
                onChange={this.setColor}
                defaultColor={this.store.waveGroup?.lineColor}
              />
              {!patient.isEmptyBed ? (
                this.props.sweepMode && (
                  <canvas
                    className="sweep-canvas"
                    ref={(c) => (this.sweepCanvas = c)}
                    key={this.canvasWorker.workerId}
                  />
                )
              ) : (
                <div className="no-patient-connected-text">
                  NO PATIENT CONNECTED
                </div>
              )}
            </div>
            <div
              {...className({
                discretes: 1,
                "color-scheme-dark": !config.FF_DARK_MODE,
              })}
            >
              {!patient.isEmptyBed
                ? this.store.discretesRow.map((discrete) => (
                    <Chiclet
                      chart={discrete}
                      key={discrete.keys.join()}
                      showTrends={false}
                    />
                  ))
                : null}
            </div>
            {config.ahi.enabled && (
              <div className="fifth-eye">
                {this.props.ahiData && (
                  <>
                    <div className="left">
                      <AhiScore
                        data={this.props.ahiData}
                        postOpsLog={this.props.surveilStore.postOpsLog}
                      />
                    </div>
                  </>
                )}
              </div>
            )}
          </React.Fragment>
        )}
      </div>
    );
  }

  get pmConfig() {
    return this.props.surveilStore.pmConfigs[this.props.patient.asmpid];
  }

  get canvasWorker() {
    return this.props.canvasWorker;
  }
  setChartRect = () => {
    if (!this.refChart || (!this.visible && this.props.sweepMode)) return;
    const { left, bottom, width, height } =
      this.refChart.getBoundingClientRect();
    if (!_.isEqual({ left, bottom, width, height }, this.chartRect)) {
      this.chartRect = { left, bottom, width, height };
      this.props.renderWorker?.postMessage({
        setChartRect: {
          asmpid: this.asmpid,
          chartRect: { left, bottom, width, height },
          seconds: config.pm.mpv.singleLeadSeconds,
        },
      });
      if (this.sweepCanvas) {
        if (
          Math.round(width) !== this.sweepCanvas.width ||
          Math.round(height) !== this.sweepCanvas.height
        ) {
          this.canvasWorker?.postMessage({
            setCanvasSize: { width, height },
            assignState: {
              pixelsPerSecond: width / config.pm.mpv.singleLeadSeconds,
            },
          });
        }
      }
    }
  };

  setChartSize() {
    const chart = this.refChart;
    this.store.chartHeight = chart.clientHeight;
    this.setChartRect();
  }

  parseNewWave({ group, measurements }) {
    this.store.measurements = measurements;

    let segments = this.store.measurements.data;
    if (!group || !segments || !segments.length) {
      return;
    }
    if (!this.refChart) return;

    segments = getUniqueOrderedSegments(segments);

    if (config.debug) this.logGaps({ segments });

    if (!this.range) {
      const finalSegment = _.get(_.last(segments), "waveData", []).find(
        (wave) => wave.key === group.key,
      );
      this.minRange = _.get(finalSegment, "minRange", group.minRange);
      const maxRange = _.get(finalSegment, "maxRange", group.maxRange);
      this.range = maxRange - this.minRange;
    }

    if (!this.firstSampleTime) {
      this.firstSampleTime = segments[0].timestamp;
    }

    const { asmpid, minRange, range, firstSampleTime } = this;

    this.props.measurementsWorker?.postMessage({
      group,
      segments: segments.map((d) => ({ ...d, waveData: undefined })),
      asmpid,
      discreteGroups: toJS(this.store?.config?.discreteGroups),
    });

    if (!this.props.sweepMode) {
      const renderWorkerPayload = {
        plotWave: {
          segments: measurements.data,
          // from this.
          minRange,
          range,
          asmpid,
          canvasWidth: this.refChart.clientWidth,
          firstSampleTime,
          // from this.store.
          chartHeight: this.store.chartHeight,
          seconds: config.pm.mpv.singleLeadSeconds,
        },
      };
      this.props?.patient?.status === "active" &&
        this.props.renderWorker?.postMessage(renderWorkerPayload);
    } else if (this.canvasWorker) {
      // Render each waveform.
      if (this.canvasTransferred && this.visible && this.store.serverTime) {
        if (segments.length) {
          const canvasWorkerMessage = {
            plotCharts: {
              waveGroups: [group],
              height: this.sweepCanvas?.height,
              width: this.sweepCanvas?.width,
              slicedWaveforms: segments,
              liveMode: true,
              pixelsPerSecond: this.pixelsPerSecond,
              chartHeight: 0,
              offsetY: 0,
              center: true,
              chartEndTime: this.store.serverTime
                .minus({ seconds: config.pm.surveilLeadBufferSeconds })
                .toISO(),
              paddingTop: 0,
            },
          };
          this.canvasWorker.postMessage(canvasWorkerMessage);
        }
      }
    }
  }
  logGaps({ segments }) {
    segments.forEach((segment) => {
      if (this.debugSampleTime) {
        console.assert(
          !DateTime.fromISO(this.debugSampleTime)
            .minus({ seconds: 1 })
            .equals(DateTime.fromISO(segment.timestamp)),
          {
            segment,
            segmentTs: segment.timestamp,
            segmentTimes: segments.map((s) => s.timestamp),
            segments,
            debugSampleTime: this.debugSampleTime,
            errorMsg: "gap detected",
          },
        );
      }
      this.debugSampleTime = segment.timestamp;
    });
  }

  get alarmTime() {
    if (this.store.highestAlarm) {
      return DateTime.fromISO(this.store.highestAlarm.localTime).toRelative();
    } else {
      return "- - - mins";
    }
  }

  get lineColor() {
    return (
      this.props.surveilStore.colorMap.getColor(this.asmpid) ||
      this.store.waveGroup?.lineColor
    );
  }

  setColor = (color) => {
    let _color = color;
    if (tinyColor.equals(this.store.waveGroup.lineColor, _color))
      _color = undefined;
    this.props.surveilStore.colorMap.setColor(this.asmpid, _color);
    this.canvasWorker?.postMessage({ assignState: { lineColor: color } });
    this.props.renderWorker?.postMessage({
      assignState: { lineColor: color, asmpid: this.asmpid },
    });
  };
}

export default inject("store", "surveilStore")(observer(SurveilPatient));
